import { router, usePage } from "@inertiajs/react"
import { useCallback, useEffect, useState } from "react"

import { BrandSplash } from "@/components/brand-splash"
import type { BrandSwitchSplashPayload } from "@/types"

/**
 * OKS-701 — global brand-switch arrival splash.
 *
 * After a creator switches crews, `CurrentBrandController#switch_creator`
 * sets a one-shot `flash[:brand_switch_splash]` (`{name, logo_url,
 * cover_url}`) for the TARGET brand and 303-redirects to that brand's
 * server-authoritative destination (missions home for active crews,
 * `/onboarding` for incomplete ones). This overlay is mounted ONCE in
 * `PersistentLayout` — which wraps both the creator shell and the wizard
 * shell — so a single mount plays the splash on whichever destination
 * the server picks (TRD KD-2 / KD-4).
 *
 * OKS-854 — the AGENCY switch now sets the same flash, so this component
 * serves both flows unchanged. The only difference is a `hold_ms` the
 * agency branch adds to the payload (a longer, more deliberate beat); the
 * creator branch omits it and keeps the shipped default. All the
 * consume-once / no-replay machinery below is flow-agnostic.
 *
 * # Why derive-during-render (not `useState` init)
 *
 * `PersistentLayout` survives Inertia visits, so this component mounts
 * ONCE and never re-mounts per navigation — a `useState(() => …flash…)`
 * initializer would only ever read the flash present at first app load.
 * Instead we open the splash when a NEW `brand_switch_splash` payload
 * arrives, using React's sanctioned "adjust state when a prop changes"
 * render-phase pattern (no effect), with a consume-once guard modelled on
 * `useFlash()`:
 *
 *   - A content signature guards against Inertia re-sharing the SAME flash
 *     on same-URL partial reloads (the OKS-491 replay path) — a re-share
 *     of an already-played payload does NOT re-open the splash.
 *   - The signature is reset on a genuine `navigate` so a later switch
 *     (even to the same brand, identical payload) plays again. Same event
 *     choice as `useFlash()`: same-URL partial reloads force
 *     `replace = true` and never fire `navigate`, so an already-played
 *     re-share stays suppressed.
 *
 * The activated payload is captured into local state, so the splash keeps
 * showing the correct brand even if `flash` changes underneath it. On
 * completion the overlay unmounts; a plain reload / back-nav of the
 * destination carries no `brand_switch_splash` flash → no replay (only the
 * switch arrival plays it — PRD/UX §3 "a plain reload isn't a switch").
 *
 * The splash itself (`<BrandSplash>`) owns the shipped lifecycle: min
 * ~600ms hold → ~400ms cross-dissolve, bounded cover-load safety timeout,
 * reduced-motion = instant swap, and `aria-hidden` decorative chrome.
 */
export function BrandSwitchSplash() {
  const { flash } = usePage()
  const payload = flash?.brand_switch_splash ?? null
  const signature = payload ? JSON.stringify(payload) : null

  const [active, setActive] = useState<BrandSwitchSplashPayload | null>(null)
  // Signature of the last payload we opened. State (not a ref) so the
  // navigate-reset re-render below is observed, and so the guard survives
  // partial reloads that re-share the same flash content.
  const [lastSignature, setLastSignature] = useState<string | null>(null)

  // Open the splash when a genuinely new payload arrives — render-phase
  // state adjustment (React "storing information from previous renders"),
  // NOT an effect, so there is no cascading-effect setState.
  if (signature && signature !== lastSignature && payload) {
    setLastSignature(signature)
    setActive(payload)
  }

  // Reset the guard on a genuine navigation so a subsequent switch — even
  // one whose payload is byte-identical (same brand) — plays again.
  useEffect(() => router.on("navigate", () => setLastSignature(null)), [])

  const dismiss = useCallback(() => setActive(null), [])

  if (!active) return null

  return (
    <BrandSplash
      brandName={active.name}
      logoUrl={active.logo_url}
      coverUrl={active.cover_url}
      // OKS-857 — portrait splash background; takes precedence over
      // `cover_url` (falls back to it, then the dark backdrop, when null).
      splashCoverUrl={active.splash_cover_url}
      // OKS-854 — server-chosen hold. The agency switch sends a longer beat
      // (`AGENCY_SPLASH_HOLD_MS`); the creator switch omits `hold_ms`, so
      // `undefined` falls through to `BrandSplash`'s shipped default and the
      // creator timing is unchanged.
      minHoldMs={active.hold_ms}
      onComplete={dismiss}
      testId="brand-switch-splash"
    />
  )
}

export default BrandSwitchSplash
